import { Component, OnInit, Input, SimpleChanges, OnChanges, Output, EventEmitter, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { TableModel } from '../../models/input/table-model';
import { TableRows } from '../../models/input/table-rows';
import { TableTemplateModel } from '../../models/template-model/table-template-model';
import { TableConfiguration } from '../../models/input/table-configuration';
import { TableCell } from '../../models/input/table-cell';
import { TableRow } from '../../models/input/table-row';
import { TableHeader } from '../../models/input/table-header';
import { TableOutputEvent } from '../../models/table-output-event';
import { TableOutputFileEvent } from '../../models/table-output-file-event';
import { TableOutputRowEvent } from '../../models/table-output-row-event';
import { TableEventTargetType } from '../../enum/table-event-target-type';
import { TableTh } from '../../models/input/table-th';
import { IconType } from 'src/app/tinea-components/icon/enum/icon-type';
import { ObjToUpdate } from '../../models/ObjToUpdate';
import { ValueCell } from '../../models/input/value-cell';
import { DisplayText } from 'src/app/models/events/displayText';
import { TableTd } from '../../models/input/table-td';
import { TableInputRowToAddOrDelete } from '../../models/input/table-input-row-to-add-or-delete';
import { IDatePickerConfig } from 'ng2-date-picker';
import { Moment } from 'moment';
import { Observable, Subscription } from 'rxjs';
import { Router } from '@angular/router';

@Component({
    selector: '.ui-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TableComponent implements OnInit, OnChanges, OnDestroy {

  @Input() tableInput: TableModel;
  @Input() trToAddOrDelete: Observable<TableInputRowToAddOrDelete>;
  private trToAddOrDeleteSub: Subscription = new Subscription();
  @Output() eventOnClick = new EventEmitter<TableOutputEvent>();
  @Output() eventOnChangeFile = new EventEmitter<TableOutputFileEvent>();
  @Output() eventOnMouseenter = new EventEmitter<TableOutputEvent>();
  @Output() eventOnMouseenterRow = new EventEmitter<TableOutputRowEvent>();
  @Output() eventOnMouseleaveRow = new EventEmitter<TableOutputRowEvent>();
  @Output() eventOnClickSelectableRow = new EventEmitter<TableOutputRowEvent>();
  @Output() eventOnAddedOrDeletedRow = new EventEmitter<TableInputRowToAddOrDelete>();
  @Output() eventOnChangedValue = new EventEmitter<ObjToUpdate>();
  @Output() multiSelectionChanged = new EventEmitter<Object[]>();
  @Output() mainButtonClicked = new EventEmitter<string>();

  readonly HEADER_ROW_ID = 'h';
  readonly ID_SEPARATOR = '_';
  readonly tableEventTargetType = TableEventTargetType;

  table: TableTemplateModel = null;
  objListToUpdateOutput: ObjToUpdate[];
  multiSelectionCell: TableCell[] = [];
  r = '/stations/4fa96896-d586-4193-bfbe-bb23e3fb29d1/dashboard';

  get iconType() { return IconType; }

  constructor(
    private router: Router
  ) {
    this.tableInput = null;
    this.table = null;
    this.objListToUpdateOutput = [];
  }

  ngOnInit(): void {
    this.trToAddOrDeleteSubHandler();
  }
  goDetail(event: Event, dataId: string) {
    if (event.target['routerLink'] && event.target['routerLink'].trim() !== '') {
      void this.router.navigateByUrl(event.target['routerLink']);
    }
    this.mainButtonClicked.emit(dataId);
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.tableInput && changes.tableInput.currentValue) {
      this.objListToUpdateOutput = [];
      this.tableMapping();
    }
  }

  ngOnDestroy(): void {
    this.trToAddOrDeleteSub.unsubscribe();
  }

  trToAddOrDeleteSubHandler(): void {
    if (this.trToAddOrDelete) {
      this.trToAddOrDeleteSub = this.trToAddOrDelete.subscribe(response => {
        this.addOrDeleteRow(response);
      });
    }
  }

  private tableMapping(): void {
    const newTable = new TableTemplateModel();
    // config
    newTable.configuration = this.configurationMapping();
    // headers
    newTable.header = this.headerMapping();
    // rows
    newTable.dataRow = this.rowMapping();

    this.table = newTable;
    this.tableValidity();

    // console.log('mapping done => ', this.table);
  }

  /**
   * Check if validity table : config errors...
   */
  tableValidity(): void {
    if (this.table.configuration.whatShowIfNoData === 'HIDE_TABLE' &&
        this.table.configuration.isDisplayedInBody() && this.table.configuration.displayMsgIfNoDataRow) {
      console.error('Error table configuration: table is configured to display "no data" message in table body AND configured to be hidden if no data');
    }
  }


  // ========================================================================== //
  //                                  MAPPING                                   //
  // ========================================================================== //
  configurationMapping(): TableConfiguration {
    const configuration = new TableConfiguration();
    configuration.displayMode = this.tableInput.configuration.displayMode;
    configuration.cssClass = this.tableInput.configuration.cssClass;
    configuration.displayMsgIfNoDataRow = this.tableInput.configuration.displayMsgIfNoDataRow;
    configuration.nullValueDisplay = this.tableInput.configuration.nullValueDisplay;
    configuration.positionMsgIfNoDataRow = this.tableInput.configuration.positionMsgIfNoDataRow;
    configuration.whatShowIfNoData = this.tableInput.configuration.whatShowIfNoData;
    configuration.headPosition = this.tableInput.configuration.headPosition;
    configuration.selectRowMode = this.tableInput.configuration.selectRowMode;
    const noDatMsg = this.tableInput.configuration.noDataRowMsg;
    configuration.noDataRowMsg = (noDatMsg) ? noDatMsg : this.tableInput.configuration.NO_DATA_ROW_MSG_DEFAULT;

    return configuration;
  }


  headerMapping(): TableHeader {
    const tableInput = this.tableInput;
    tableInput.header.row.id = this.generateAndGetRowIdForHeader();
    const cells = this.cellMapping(tableInput.header.cells, tableInput.header.cells) as TableTh[];

    const tableHeader = new TableHeader();
    tableHeader.cells = cells;
    tableHeader.row = tableInput.header.row;
    return tableHeader;
  }


  rowMapping(): TableRows[] {
    const dataRowMapped = this.tableInput.dataRow.map((dataRow, i) => {
      dataRow.row.id = this.generateAndGetRowIdForDataRow(i);
      const cells = this.cellMapping(this.tableInput.header.cells, dataRow.cells);
      dataRow.cells = cells;
      return dataRow;
    });

    return dataRowMapped;
  }

  cellMapping(cellThList: TableTh[], cells: TableCell[]): TableCell[] {
    const cellsMapped = cells.map((cell, i) => {
        cell.columnId = cell.columnId ? cell.columnId : this.generateAndGetCellColumnId(cellThList[i]);
        return cell;
    });
    return cellsMapped;
  }

  /**
   * Generate an Id for each cell.
   * Format : columnId
   */
  generateAndGetCellColumnId(cellTh: TableTh): string {
    return cellTh ? cellTh.columnId : null;
  }

  /**
   * Generate an Id for the header row. set to '0' cause: it's the first row of table
   */
  generateAndGetRowIdForHeader(): string {
    return this.HEADER_ROW_ID;
  }

  /**
   * Generate an Id for the data rows
   * @param i DataRow index
   */
  generateAndGetRowIdForDataRow(i: number): string {
    return i.toString();
  }

  updateObListToUpdate(dataId: string, dataType: 'STRING' | 'DATE' | 'NUMBER' | 'BUTTON' | 'ANY',
                       oldValueCell: ValueCell, newValue: any, cell: TableTd, dataRow: TableRows): void {
    const objListToUpdateOutput: ObjToUpdate[] = JSON.parse(JSON.stringify( this.objListToUpdateOutput ));
    const indexExistingObj = objListToUpdateOutput.findIndex(o => o.row.id === dataRow.row.id && o.columnId === cell.columnId);

    let objListWithoutOldValue = objListToUpdateOutput;
    if (indexExistingObj >= 0) {
      objListWithoutOldValue = objListToUpdateOutput.filter((o, i) => i !== indexExistingObj);
    }

    const objToUpdate = new ObjToUpdate();
    objToUpdate.id = dataId;
    objToUpdate.columnId = cell.columnId;
    objToUpdate.row = dataRow.row;
    objToUpdate.oldValueCell = oldValueCell;
    objToUpdate.newValue = this.returnValueWithType(dataType, newValue);
    objToUpdate.type = oldValueCell.dataType;
    objListWithoutOldValue.push(objToUpdate);

    this.objListToUpdateOutput = objListWithoutOldValue;
    this.eventOnChangedValue.emit(objToUpdate);
  }

  onChangeSelect(dataId: string, dataType: 'STRING' | 'DATE' | 'NUMBER' | 'BUTTON' | 'ANY',
                 oldValueCell: ValueCell, newValue: DisplayText, cell: TableTd, dataRow: TableRows): void {
    this.updateObListToUpdate(dataId, dataType, oldValueCell, newValue.key, cell, dataRow);
  }

  onChangeDatePicker(dataId: string, dataType: 'STRING' | 'DATE' | 'NUMBER' | 'BUTTON' | 'ANY',
                     oldValueCell: ValueCell, newValue: Moment, cell: TableTd, dataRow: TableRows): void {
    if (newValue !== undefined) {
      const newValueString = newValue.format(oldValueCell.datePickerConfig.format);
      this.updateObListToUpdate(dataId, dataType, oldValueCell, newValueString, cell, dataRow);
    }
  }

  returnValueWithType(dataType: 'STRING' | 'DATE' | 'NUMBER' | 'BUTTON' | 'ANY' |  'BOOLEAN', value: string| number): any {
    switch (dataType) {
      case 'STRING':
        return (value) ? value.toString() : null;
      case 'DATE':
        return (value) ? value.toString() : null;
      case 'BOOLEAN':
        return value;
      case 'NUMBER':
        if (!isNaN(Number(value))) {
          return Number(value);
        } else {
            console.warn('returnValueWithType() : Not a Number');
        }
        break;
      default:
        console.warn('returnValueWithType: a type is not defined');
        return null;
    }
    return null;
  }

  addOrDeleteRow(trToAddOrDelete: TableInputRowToAddOrDelete): void {
    if (trToAddOrDelete.isAdd) {
      this.table.dataRow.splice(trToAddOrDelete.index, 0, trToAddOrDelete.tr);
      this.tableInput.dataRow.splice(trToAddOrDelete.index, 0, trToAddOrDelete.tr);
    } else {
      this.table.dataRow.splice(trToAddOrDelete.index, 1);
      this.tableInput.dataRow.splice(trToAddOrDelete.index, 1);
    }
    this.updateIdRows();
    this.eventOnAddedOrDeletedRow.emit(trToAddOrDelete);
  }

  updateIdRows(): void {
    this.table.dataRow.map((dataRow, i) => {
      dataRow.row.id = this.generateAndGetRowIdForDataRow(i);
    });
  }



  // ========================================================================== //
  //                                   EVENTS                                   //
  // ========================================================================== //
  onClick(row: TableRow, cell: any, target: any = null, event): void {
    const output = new TableOutputEvent();
    output.row = row;
    output.cell = cell;
    output.target = target;
    output.type = 'CLICK';
    output.targetHtml = event;
    output.objListToUpdate = this.objListToUpdateOutput;

    this.eventOnClick.emit(output);
  }

  onChangeInputFile(row: TableRow, cell: any, target: any = null, files: FileList): void {
    const output = new TableOutputFileEvent();
    output.row = row;
    output.cell = cell;
    output.target = target;
    output.type = 'CHANGE';
    output.objListToUpdate = this.objListToUpdateOutput;
    output.files = files;

    this.eventOnChangeFile.emit(output);
  }

  onMouseenter(row: TableRow, cell: any, target: any = null, rowType: TableEventTargetType): void {
    const output = new TableOutputEvent();
    output.row = row;
    output.cell = cell;
    output.target = target;
    output.type = 'MOUSEENTER';
    output.targetType = rowType;

    this.eventOnMouseenter.emit(output);
  }

  onMouseenterRow(row: TableRow, rowType: TableEventTargetType): void {
    const output = new TableOutputRowEvent();
    output.row = row;
    output.type = 'MOUSEENTER';
    output.targetType = rowType;

    this.eventOnMouseenterRow.emit(output);
  }

  onMouseLeaveRow(row: TableRow, rowType: TableEventTargetType): void {
    const output = new TableOutputRowEvent();
    output.row = row;
    output.type = 'MOUSE_LEAVE';
    output.targetType = rowType;

    this.eventOnMouseleaveRow.emit(output);
  }

  onClickRow(dataRow: TableRows, rowType: TableEventTargetType): void {
    this.onClickSelectableRowHandler(dataRow.row, rowType);
  }

  onClickSelectableRowHandler(row: TableRow, rowType: TableEventTargetType): void {
    if (row.selectable && !row.isDisabled) {
      switch (this.table.configuration.selectRowMode) {
        case 'NONE':
          break;
        case 'UNIQUE':
          this.unselelectAllRows();
          this.toggleSelectableRow(row, rowType);
          break;
        case 'MULTI':
          this.toggleSelectableRow(row, rowType);
          break;
      }
    }
  }

  unselelectAllRows(): void {
    this.table.unselectAllRows();
  }

  toggleSelectableRow(row: TableRow, rowType: TableEventTargetType): void {
    row.isSelected = !row.isSelected;
    const output = new TableOutputRowEvent();
    output.row = row;
    output.type = 'CLICK';
    output.targetType = rowType;
    this.eventOnClickSelectableRow.emit(output);
  }

  onSelectRow(tableCell: TableCell, isHeader: boolean): void {
    console.log(tableCell);
    const checked = !tableCell.valueCells[0].isChecked;
    if (isHeader) {
      this.tableInput.dataRow.forEach(row => {
        this.checkedCell(row.cells[0], checked);
      });
    }
    this.checkedCell(tableCell, checked, isHeader);
    this.multiSelectionChanged.emit(this.multiSelectionCell.map(cell => cell.getFirst().data));
  }

  checkedCell(tableCell: TableCell, checked: boolean, isHeader = false): void {
    tableCell.getFirst().isChecked = checked;
    if (!isHeader) {
      const index = this.multiSelectionCell.indexOf(tableCell);
      if (checked && index === -1) {
        this.multiSelectionCell.push(tableCell);
      } else if (!checked && index !== -1) {
        this.multiSelectionCell.splice(index, 1);
      }
    }
  }
}
